import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2, RefreshCw, ShieldCheck, ShieldAlert, Activity, Send, AlertTriangle,
  CheckCircle2, Clock, Bell, ExternalLink, Eye, FileDown, RotateCw, Webhook, Lock,
} from "lucide-react";
import {
  adminCheckEmailDns, adminRunEmailE2ETest, adminEmailAuditTail, adminEmailAlerts,
  adminStepUpStatus, adminStepUpVerify, adminInspectTemplate, adminExportEmailLogCsv,
  adminListDlq, adminReplayDlq, adminListWebhookEvents, adminReplayWebhookEvent,
} from "@/lib/admin-email-ops.functions";
import { adminCheckRole } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/email-ops")({
  head: () => ({
    meta: [
      { title: "Email operations — Admin — Peopels" },
      { name: "robots", content: "noindex" },
    ],
  }),
  // Strict RBAC: refuse non-admins at the route level (server fns also enforce).
  beforeLoad: async () => {
    try {
      const { isAdmin } = await adminCheckRole();
      if (!isAdmin) throw redirect({ to: "/dashboard" });
    } catch (e: any) {
      if (e?.isRedirect) throw e;
      throw redirect({ to: "/dashboard" });
    }
  },
  component: AdminEmailOps,
});

type DnsResult = Awaited<ReturnType<typeof adminCheckEmailDns>>;
type AlertResult = Awaited<ReturnType<typeof adminEmailAlerts>>;
type AuditResult = Awaited<ReturnType<typeof adminEmailAuditTail>>;
type E2EResult = Awaited<ReturnType<typeof adminRunEmailE2ETest>>;
type InspectResult = Awaited<ReturnType<typeof adminInspectTemplate>>;
type DlqResult = Awaited<ReturnType<typeof adminListDlq>>;
type WebhookResult = Awaited<ReturnType<typeof adminListWebhookEvents>>;

function fmt(ts: string | null | undefined) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "medium" });
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="outline">unknown</Badge>;
  if (status === "sent") return <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">sent</Badge>;
  if (status === "dlq" || status === "failed") return <Badge variant="destructive">{status}</Badge>;
  if (status === "pending") return <Badge className="border-sky-500/30 bg-sky-500/10 text-sky-700 hover:bg-sky-500/10">pending</Badge>;
  if (status === "suppressed") return <Badge className="border-amber-500/30 bg-amber-500/15 text-amber-800 hover:bg-amber-500/15">suppressed</Badge>;
  if (status === "replayed") return <Badge className="border-violet-500/30 bg-violet-500/10 text-violet-700 hover:bg-violet-500/10">replayed</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function DnsStateBadge({ state }: { state: DnsResult["state"] }) {
  if (state === "active") return <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">Active</Badge>;
  if (state === "propagating") return <Badge className="border-sky-500/30 bg-sky-500/15 text-sky-700 hover:bg-sky-500/15">Propagating</Badge>;
  if (state === "partial") return <Badge className="border-amber-500/30 bg-amber-500/15 text-amber-800 hover:bg-amber-500/15">Partial</Badge>;
  return <Badge variant="destructive">Missing</Badge>;
}

// ===== Step-up gate =====
function useStepUp() {
  const status = useServerFn(adminStepUpStatus);
  const verify = useServerFn(adminStepUpVerify);
  const [valid, setValid] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [resolver, setResolver] = useState<((ok: boolean) => void) | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await status();
      setValid(r.valid);
      setExpiresAt(r.expiresAt);
    } catch { /* ignore */ }
  }, [status]);

  useEffect(() => { void refresh(); }, [refresh]);

  // require(): if not valid, pop modal and return promise resolving on success/cancel
  const require = useCallback((): Promise<boolean> => {
    return new Promise((res) => {
      // Recheck status synchronously via cached `valid`; refresh first for accuracy.
      status().then((r) => {
        setValid(r.valid); setExpiresAt(r.expiresAt);
        if (r.valid) return res(true);
        setOpen(true); setPassword(""); setErr(null);
        setResolver(() => res);
      }).catch(() => {
        setOpen(true); setPassword(""); setErr(null);
        setResolver(() => res);
      });
    });
  }, [status]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await verify({ data: { password } });
      setValid(true); setExpiresAt(r.expiresAt);
      setOpen(false);
      resolver?.(true); setResolver(null);
    } catch (e: any) {
      const msg = String(e?.message ?? e);
      setErr(msg.includes("invalid_password") ? "Incorrect password." : msg);
    } finally {
      setBusy(false);
    }
  }

  function onCancel() {
    setOpen(false);
    resolver?.(false); setResolver(null);
  }

  const modal = (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" /> Re-confirm your password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <p className="text-sm text-muted-foreground">
            This action is sensitive. Re-enter your password to authorize it for the next 10 minutes.
          </p>
          <div>
            <Label htmlFor="su-pw">Password</Label>
            <Input
              id="su-pw"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="mt-1"
            />
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={busy || !password}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              <span className="ml-2">Verify</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return { valid, expiresAt, require, refresh, modal };
}

function AdminEmailOps() {
  const checkDns = useServerFn(adminCheckEmailDns);
  const runE2E = useServerFn(adminRunEmailE2ETest);
  const tail = useServerFn(adminEmailAuditTail);
  const alerts = useServerFn(adminEmailAlerts);
  const inspect = useServerFn(adminInspectTemplate);
  const exportCsv = useServerFn(adminExportEmailLogCsv);
  const listDlq = useServerFn(adminListDlq);
  const replayDlq = useServerFn(adminReplayDlq);
  const listHooks = useServerFn(adminListWebhookEvents);
  const replayHook = useServerFn(adminReplayWebhookEvent);
  const stepUp = useStepUp();

  // DNS / alerts / audit
  const [dns, setDns] = useState<DnsResult | null>(null);
  const [dnsLoading, setDnsLoading] = useState(false);
  const [alertState, setAlertState] = useState<AlertResult | null>(null);
  const [alertLoading, setAlertLoading] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [e2eBusy, setE2eBusy] = useState(false);
  const [e2eResult, setE2eResult] = useState<E2EResult | null>(null);
  const [audit, setAudit] = useState<AuditResult | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Template inspector
  const [tpl, setTpl] = useState<string>("");
  const [tplDataText, setTplDataText] = useState<string>("");
  const [tplInspect, setTplInspect] = useState<InspectResult | null>(null);
  const [tplBusy, setTplBusy] = useState(false);

  // CSV export
  const [csvDays, setCsvDays] = useState(7);
  const [csvTemplate, setCsvTemplate] = useState("");
  const [csvStatus, setCsvStatus] = useState("");
  const [csvDomain, setCsvDomain] = useState("");
  const [csvFrom, setCsvFrom] = useState("");
  const [csvTo, setCsvTo] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvSummary, setCsvSummary] = useState<string | null>(null);

  // DLQ
  const [dlq, setDlq] = useState<DlqResult | null>(null);
  const [dlqSel, setDlqSel] = useState<Set<string>>(new Set());
  const [dlqBusy, setDlqBusy] = useState(false);
  const [dlqResults, setDlqResults] = useState<Awaited<ReturnType<typeof adminReplayDlq>>["results"] | null>(null);

  // Webhooks
  const [hooks, setHooks] = useState<WebhookResult | null>(null);
  const [hookFilter, setHookFilter] = useState<"all" | "errors">("errors");
  const [hookExpanded, setHookExpanded] = useState<string | null>(null);
  const [hookBusy, setHookBusy] = useState<string | null>(null);

  // === loaders ===
  const loadDns = useCallback(async () => {
    setDnsLoading(true);
    try { setDns(await checkDns()); } catch (e) { setErr(String(e)); } finally { setDnsLoading(false); }
  }, [checkDns]);

  const loadAlerts = useCallback(async () => {
    setAlertLoading(true);
    try { setAlertState(await alerts()); } catch (e) { setErr(String(e)); } finally { setAlertLoading(false); }
  }, [alerts]);

  const loadAudit = useCallback(async (sinceIso?: string, recipientEmail?: string) => {
    setAuditLoading(true);
    try {
      const since = sinceIso ?? new Date(Date.now() - 24 * 60 * 60_000).toISOString();
      setAudit(await tail({ data: { sinceIso: since, recipientEmail, limit: 100 } }));
    } catch (e) { setErr(String(e)); } finally { setAuditLoading(false); }
  }, [tail]);

  const loadDlq = useCallback(async () => {
    try { setDlq(await listDlq({ data: { limit: 100 } })); } catch (e) { setErr(String(e)); }
  }, [listDlq]);

  const loadHooks = useCallback(async () => {
    try {
      setHooks(await listHooks({ data: { days: 7, onlyErrors: hookFilter === "errors", limit: 100 } }));
    } catch (e) { setErr(String(e)); }
  }, [listHooks, hookFilter]);

  useEffect(() => {
    void loadDns(); void loadAlerts(); void loadAudit(); void loadDlq(); void loadHooks();
    const t = setInterval(() => { void loadAlerts(); }, 30_000);
    return () => clearInterval(t);
  }, [loadDns, loadAlerts, loadAudit, loadDlq, loadHooks]);

  useEffect(() => { void loadHooks(); }, [hookFilter, loadHooks]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.trim());

  // === E2E test (step-up gated) ===
  async function onRunE2E() {
    if (!validEmail) return;
    const ok = await stepUp.require();
    if (!ok) return;
    setE2eBusy(true); setE2eResult(null); setErr(null);
    try {
      const r = await runE2E({ data: { recipientEmail: recipient.trim() } });
      setE2eResult(r);
      const start = r.startedAt;
      for (let i = 0; i < 6; i++) {
        await new Promise((res) => setTimeout(res, 2500));
        await loadAudit(start, r.recipient);
      }
    } catch (e) { setErr(String(e)); } finally { setE2eBusy(false); }
  }

  // === Template inspect ===
  async function onInspect() {
    if (!tpl) return;
    setTplBusy(true); setErr(null);
    try {
      let data: Record<string, unknown> | undefined;
      if (tplDataText.trim()) {
        try { data = JSON.parse(tplDataText); } catch { throw new Error("Invalid JSON in template data"); }
      }
      const r = await inspect({ data: { templateName: tpl, templateData: data } });
      setTplInspect(r);
      if (!tplDataText.trim()) setTplDataText(JSON.stringify(r.effectiveData, null, 2));
    } catch (e) { setErr(String(e)); } finally { setTplBusy(false); }
  }
  // Lazy-load registered templates on first open
  useEffect(() => {
    if (tplInspect || tpl) return;
    (async () => {
      try {
        const r = await inspect({ data: { templateName: "booking-completed" } });
        setTpl("booking-completed");
        setTplInspect(r);
        setTplDataText(JSON.stringify(r.effectiveData, null, 2));
      } catch { /* ignore */ }
    })();
  }, [inspect, tpl, tplInspect]);

  // === CSV export ===
  async function onExportCsv() {
    setCsvBusy(true); setErr(null); setCsvSummary(null);
    try {
      const r = await exportCsv({
        data: {
          days: csvDays,
          template: csvTemplate || undefined,
          status: csvStatus || undefined,
          recipientDomain: csvDomain || undefined,
          from: csvFrom ? new Date(csvFrom).toISOString() : undefined,
          to: csvTo ? new Date(csvTo).toISOString() : undefined,
          limit: 50000,
        },
      });
      const blob = new Blob([r.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = r.filename; a.click();
      URL.revokeObjectURL(url);
      setCsvSummary(`Downloaded ${r.rowCount} rows as ${r.filename}.`);
    } catch (e) { setErr(String(e)); } finally { setCsvBusy(false); }
  }

  // === DLQ replay (step-up gated) ===
  const dlqSelectedIds = useMemo(() => Array.from(dlqSel), [dlqSel]);
  function toggleDlq(id: string) {
    const next = new Set(dlqSel);
    if (next.has(id)) next.delete(id); else next.add(id);
    setDlqSel(next);
  }
  function selectAllDlq(checked: boolean) {
    if (!checked || !dlq) return setDlqSel(new Set());
    setDlqSel(new Set(dlq.rows.slice(0, 50).map((r) => r.message_id!).filter(Boolean)));
  }
  const [dlqDryRun, setDlqDryRun] = useState(false);
  async function onReplayDlq() {
    if (dlqSelectedIds.length === 0) return;
    // Dry-run is read-only — no step-up needed.
    if (!dlqDryRun) {
      const ok = await stepUp.require();
      if (!ok) return;
    }
    setDlqBusy(true); setErr(null); setDlqResults(null);
    try {
      const r = await replayDlq({ data: { messageIds: dlqSelectedIds, dryRun: dlqDryRun } });
      setDlqResults(r.results);
      if (!dlqDryRun) {
        setDlqSel(new Set());
        await loadDlq();
      }
    } catch (e) { setErr(String(e)); } finally { setDlqBusy(false); }
  }


  // === Webhook replay (step-up gated) ===
  async function onReplayWebhook(errorId: string) {
    const ok = await stepUp.require();
    if (!ok) return;
    setHookBusy(errorId); setErr(null);
    try {
      await replayHook({ data: { errorId } });
      await loadHooks();
    } catch (e) { setErr(String(e)); } finally { setHookBusy(null); }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Email operations</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              DNS, template inspector, end-to-end test, audit log, CSV export, DLQ replay, webhook inspector.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs">
              <ShieldCheck className={`h-3 w-3 ${stepUp.valid ? "text-emerald-600" : "text-muted-foreground"}`} />
              {stepUp.valid
                ? `Step-up active until ${stepUp.expiresAt ? new Date(stepUp.expiresAt).toLocaleTimeString("en-GB") : ""}`
                : "Step-up required for critical actions"}
            </span>
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/emails">Full log <ExternalLink className="ml-1 h-3.5 w-3.5" /></Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/ops">Operations <ExternalLink className="ml-1 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
        </div>

        {err && (
          <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{err}</p>
        )}

        {/* === ALERTS === */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
              <Bell className="h-4 w-4" /> Live alerts <span className="text-xs font-normal text-muted-foreground">refreshes every 30s</span>
            </h2>
            <Button variant="ghost" size="sm" onClick={loadAlerts} disabled={alertLoading}>
              {alertLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
          {alertState && alertState.alerts.length === 0 ? (
            <p className="mt-3 flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> All clear over the last {alertState.windowMinutes}m —
              {" "}{alertState.counts.sent}/{alertState.counts.total} sent, {alertState.counts.dlq} in DLQ.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {alertState?.alerts.map((a) => (
                <li key={a.id} className={`flex items-start gap-3 rounded-md border p-3 text-sm ${a.severity === "critical" ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200"}`}>
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div><p className="font-medium">{a.title}</p><p className="text-xs opacity-90">{a.detail}</p></div>
                </li>
              ))}
            </ul>
          )}
          {alertState && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {[
                { k: "total", v: alertState.counts.total, label: "Unique (60m)" },
                { k: "sent", v: alertState.counts.sent, label: "Sent" },
                { k: "pending", v: alertState.counts.pending, label: "Pending" },
                { k: "dlq", v: alertState.counts.dlq, label: "DLQ" },
                { k: "rate", v: `${(alertState.counts.failureRate * 100).toFixed(1)}%`, label: "Failure rate" },
              ].map((s) => (
                <div key={s.k} className="rounded-lg border border-border bg-background p-3">
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <p className="mt-0.5 text-xl font-semibold tabular-nums">{s.v}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* === DNS === */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
              {dns?.state === "active" ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldAlert className="h-4 w-4 text-amber-600" />} Sender domain
            </h2>
            <Button variant="ghost" size="sm" onClick={loadDns} disabled={dnsLoading}>
              {dnsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1">Re-check</span>
            </Button>
          </div>
          {dns ? (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{dns.domain}</span>
                  <DnsStateBadge state={dns.state} />
                </div>
                <p className="text-xs text-muted-foreground">Last checked {fmt(dns.checkedAt)}</p>
              </div>
              <div className="space-y-2 text-sm">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Required NS</p>
                <ul className="space-y-1 font-mono text-xs">
                  {dns.expectedNs.map((e) => (
                    <li key={e} className={dns.observedNs.includes(e) ? "text-emerald-700" : "text-muted-foreground"}>
                      {dns.observedNs.includes(e) ? "✓" : "·"} {e}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">MX: {dns.mxRecords.length || "none"} · SPF: {dns.spfRecords.length ? "present" : "missing"}</p>
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground"><Loader2 className="inline h-3 w-3 animate-spin" /> Checking DNS…</p>
          )}
        </section>

        {/* === E2E TEST === */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
            <Send className="h-4 w-4" /> One-click end-to-end test
            <Badge variant="outline" className="ml-2"><Lock className="mr-1 h-3 w-3" />step-up required</Badge>
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Sends signup/invite, password reset, magic link, booking confirmation, and review request, then tails the log.
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[280px] flex-1">
              <Label htmlFor="rcpt">Recipient</Label>
              <Input id="rcpt" type="email" placeholder="you@peopels.com" value={recipient}
                onChange={(e) => setRecipient(e.target.value)} className="mt-1" />
            </div>
            <Button onClick={onRunE2E} disabled={!validEmail || e2eBusy}>
              {e2eBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              <span className="ml-2">Run end-to-end test</span>
            </Button>
          </div>
          {e2eResult && (
            <div className="mt-4 overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Trigger</th><th className="px-3 py-2 text-left">Error</th></tr>
                </thead>
                <tbody>
                  {e2eResult.results.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{r.label}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.kind}</td>
                      <td className="px-3 py-2">{r.ok ? <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">queued</Badge> : <Badge variant="destructive">failed</Badge>}</td>
                      <td className="px-3 py-2 text-xs text-destructive">{r.error ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* === TEMPLATE INSPECTOR === */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
            <Eye className="h-4 w-4" /> Template preview &amp; variables inspector
          </h2>
          <div className="mt-4 grid gap-4 lg:grid-cols-[320px_1fr]">
            <div className="space-y-3">
              <div>
                <Label htmlFor="tpl">Template</Label>
                <select id="tpl" className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                  value={tpl} onChange={(e) => { setTpl(e.target.value); setTplDataText(""); setTplInspect(null); }}>
                  {(tplInspect?.registeredTemplates ?? [tpl].filter(Boolean)).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="tdata">Template data (JSON)</Label>
                <textarea id="tdata" rows={10} value={tplDataText} onChange={(e) => setTplDataText(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs" />
              </div>
              <Button onClick={onInspect} disabled={tplBusy} className="w-full">
                {tplBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">Re-render preview</span>
              </Button>
              {tplInspect && (
                <div className="rounded-md border border-border bg-background p-3 text-xs">
                  <p className="font-semibold">Subject</p>
                  <p className="mt-1 font-mono">{tplInspect.subject}</p>
                  <p className="mt-3 font-semibold">Outgoing headers</p>
                  <dl className="mt-1 space-y-0.5 font-mono">
                    {Object.entries(tplInspect.headers).map(([k, v]) => (
                      <div key={k} className="grid grid-cols-[110px_1fr] gap-2">
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd className="break-all">{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
            <div className="overflow-hidden rounded-lg border border-border bg-white">
              {tplInspect ? (
                <iframe title="Template preview" srcDoc={tplInspect.html} sandbox="" className="h-[640px] w-full" />
              ) : (
                <div className="grid h-[640px] place-items-center text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* === CSV EXPORT === */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
            <FileDown className="h-4 w-4" /> Export delivery log (CSV)
          </h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label>Range (days)</Label>
              <select value={csvDays} onChange={(e) => setCsvDays(Number(e.target.value))}
                className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm">
                {[1, 7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>Last {d} day{d > 1 ? "s" : ""}</option>)}
              </select>
            </div>
            <div>
              <Label>Template</Label>
              <select value={csvTemplate} onChange={(e) => setCsvTemplate(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm">
                <option value="">All</option>
                {tplInspect?.registeredTemplates.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select value={csvStatus} onChange={(e) => setCsvStatus(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm">
                <option value="">All</option>
                <option value="sent">Sent</option>
                <option value="pending">Pending</option>
                <option value="dlq">DLQ</option>
                <option value="failed">Failed</option>
                <option value="suppressed">Suppressed</option>
                <option value="bounced">Bounced</option>
                <option value="complained">Complained</option>
                <option value="replayed">Replayed</option>
              </select>
            </div>
            <div>
              <Label>Recipient domain</Label>
              <Input value={csvDomain} onChange={(e) => setCsvDomain(e.target.value)} placeholder="gmail.com" className="mt-1" />
            </div>
            <div>
              <Label>From</Label>
              <Input type="datetime-local" value={csvFrom} onChange={(e) => setCsvFrom(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="datetime-local" value={csvTo} onChange={(e) => setCsvTo(e.target.value)} className="mt-1" />
            </div>
            <div className="flex items-end">
              <Button onClick={onExportCsv} disabled={csvBusy} className="w-full">
                {csvBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                <span className="ml-2">Download CSV</span>
              </Button>
            </div>
          </div>
          {csvSummary && <p className="mt-3 text-xs text-emerald-700">{csvSummary}</p>}
        </section>

        {/* === DLQ REPLAY === */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
              <RotateCw className="h-4 w-4" /> Dead-letter queue
              <Badge variant="outline" className="ml-2"><Lock className="mr-1 h-3 w-3" />step-up required</Badge>
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs">
                <Checkbox checked={dlqDryRun} onCheckedChange={(c) => setDlqDryRun(Boolean(c))} />
                Dry run (preview only)
              </label>
              <Button variant="ghost" size="sm" onClick={loadDlq}><RefreshCw className="h-4 w-4" /></Button>
              <Button size="sm" variant={dlqDryRun ? "outline" : "default"} onClick={onReplayDlq} disabled={dlqBusy || dlqSelectedIds.length === 0}>
                {dlqBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
                <span className="ml-2">{dlqDryRun ? "Preview" : "Replay"} {dlqSelectedIds.length || ""}</span>
              </Button>
            </div>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Replay is rate-limited (max 50/batch, 10/sec), idempotent by message id, refuses suppressed recipients,
            and is logged to audit_logs. Dry run renders every template and checks suppressions without sending —
            no step-up required.
          </p>

          <div className="mt-4 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="w-8 px-3 py-2"><Checkbox
                    checked={dlqSel.size > 0 && dlq?.rows.length === dlqSel.size}
                    onCheckedChange={(c) => selectAllDlq(Boolean(c))} /></th>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-left">Recipient</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Error</th>
                </tr>
              </thead>
              <tbody>
                {dlq && dlq.rows.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    <CheckCircle2 className="mx-auto mb-1 h-5 w-5 text-emerald-600" /> DLQ is empty.
                  </td></tr>
                )}
                {dlq?.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={dlqSel.has(r.message_id ?? "")}
                        onCheckedChange={() => r.message_id && toggleDlq(r.message_id)}
                        disabled={!r.message_id}
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2">{r.template_name ?? "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.recipient_email ?? "—"}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-3 py-2 text-xs text-destructive">{r.error_message ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dlqResults && (
            <div className="mt-4 rounded-md border border-border bg-background p-3 text-xs">
              <p className="font-semibold">
                {dlqDryRun ? "Dry-run preview" : "Replay results"}
                {dlqDryRun && (() => {
                  const wouldSend = dlqResults.filter((r) => r.wouldSend).length;
                  const blocked = dlqResults.length - wouldSend;
                  return (
                    <span className="ml-2 font-normal text-muted-foreground">
                      — {wouldSend} would send, {blocked} blocked
                    </span>
                  );
                })()}
              </p>
              <ul className="mt-2 space-y-1">
                {dlqResults.map((r) => {
                  const isOk = dlqDryRun ? r.wouldSend : r.ok;
                  return (
                    <li key={r.messageId} className={isOk ? "text-emerald-700" : "text-destructive"}>
                      <div>
                        {isOk ? "✓" : "✗"} <span className="font-mono">{r.messageId}</span>
                        {!dlqDryRun && r.ok && <> → <span className="font-mono">{r.newMessageId}</span></>}
                        {!isOk && r.error && <> — {r.error}</>}
                      </div>
                      {dlqDryRun && (
                        <div className="ml-4 mt-0.5 text-[11px] text-muted-foreground">
                          {r.template ?? "?"} → {r.recipient ?? "?"}
                          {r.subject && <> · subject: <span className="text-foreground">{r.subject}</span></>}
                          {r.suppressed && <> · <span className="text-destructive">suppressed{r.suppressionReason ? ` (${r.suppressionReason})` : ""}</span></>}
                          {r.renderOk === false && <> · <span className="text-destructive">render failed</span></>}
                          {r.renderOk === true && !r.suppressed && <> · render ok</>}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>


        {/* === WEBHOOK INSPECTOR === */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
              <Webhook className="h-4 w-4" /> Webhook delivery inspector
              <Badge variant="outline" className="ml-2"><Lock className="mr-1 h-3 w-3" />step-up for replay</Badge>
            </h2>
            <div className="flex gap-2">
              <div className="inline-flex rounded-full border border-border p-1 text-xs">
                <button onClick={() => setHookFilter("errors")} className={`rounded-full px-3 py-1 ${hookFilter === "errors" ? "bg-foreground text-background" : ""}`}>Errors</button>
                <button onClick={() => setHookFilter("all")} className={`rounded-full px-3 py-1 ${hookFilter === "all" ? "bg-foreground text-background" : ""}`}>All</button>
              </div>
              <Button variant="ghost" size="sm" onClick={loadHooks}><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {hooks && hooks.errors.length === 0 && hooks.successes.length === 0 && (
              <p className="rounded-md border border-border bg-background p-6 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="mx-auto mb-1 h-5 w-5 text-emerald-600" /> No webhook activity in window.
              </p>
            )}
            {hooks?.errors.map((e) => {
              const open = hookExpanded === e.id;
              return (
                <div key={e.id} className="rounded-lg border border-border">
                  <button className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm"
                    onClick={() => setHookExpanded(open ? null : e.id)}>
                    <div className="flex items-center gap-2">
                      {e.resolved
                        ? <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">resolved</Badge>
                        : <Badge variant="destructive">error</Badge>}
                      <span className="font-mono text-xs">{e.source}</span>
                      <span className="font-medium">{e.event_type ?? "?"}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{e.event_id ?? "no-id"}</span>
                      {e.status_code && <span className="text-xs text-muted-foreground">HTTP {e.status_code}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{fmt(e.created_at)}</span>
                      {!e.resolved && (
                        <Button size="sm" variant="outline" disabled={hookBusy === e.id}
                          onClick={(ev) => { ev.stopPropagation(); void onReplayWebhook(e.id); }}>
                          {hookBusy === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
                          <span className="ml-1">Replay</span>
                        </Button>
                      )}
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-border bg-muted/20 p-3 text-xs">
                      <p className="text-destructive">{e.error_message}</p>
                      <pre className="mt-2 max-h-72 overflow-auto rounded bg-background p-2 font-mono text-[10px]">
                        {JSON.stringify(e.payload, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
            {hookFilter === "all" && hooks?.successes.map((s) => {
              const open = hookExpanded === s.event_id;
              return (
                <div key={s.event_id} className="rounded-lg border border-border">
                  <button className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm"
                    onClick={() => setHookExpanded(open ? null : s.event_id)}>
                    <div className="flex items-center gap-2">
                      <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">delivered</Badge>
                      <span className="font-mono text-xs">stripe</span>
                      <span className="font-medium">{s.event_type}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{s.event_id}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{fmt(s.received_at)}</span>
                  </button>
                  {open && (
                    <div className="border-t border-border bg-muted/20 p-3 text-xs">
                      <pre className="max-h-72 overflow-auto rounded bg-background p-2 font-mono text-[10px]">
                        {JSON.stringify(s.payload, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* === AUDIT TAIL === */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
              <Activity className="h-4 w-4" /> Delivery audit (last 24h)
            </h2>
            <Button variant="ghost" size="sm" onClick={() => loadAudit()} disabled={auditLoading}>
              {auditLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
          <div className="mt-3 overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-left">Recipient</th><th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Message id</th><th className="px-3 py-2 text-left">Error</th></tr>
              </thead>
              <tbody>
                {audit && audit.rows.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    <Clock className="mx-auto mb-2 h-5 w-5" /> No emails in this window.
                  </td></tr>
                )}
                {audit?.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2">{r.template_name ?? "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.recipient_email ?? "—"}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{r.message_id ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-destructive">{r.error_message ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
      <Footer />
      {stepUp.modal}
    </div>
  );
}
